import { HazardPercentList } from "./HazardPercentList";
import { PendingScore } from "./PendingScore";
import { hazardKeyLabel, type AssetHazardResult, type HazardKey } from "@/lib/results";
import { computeTakeaways, type PerHazardHighRisk } from "@/lib/takeaways";
import type { Asset } from "@/lib/assets";

interface TakeawayCardsProps {
  results: AssetHazardResult[];
  assets: Asset[];
  /** Workspace's per-stage importance ratings, keyed by value chain stage
   * id (client/src/lib/assets.ts's getWorkspaceValueChainImportance()). */
  stageImportance: Record<string, number>;
}

interface Takeaway {
  /** Short uppercase eyebrow, redesign treatment. */
  label: string;
  value: string | null;
  /** The spec's own wording for this takeaway (§3 Tab 2, verbatim) — kept
   * on the card so the eyebrow can be short without losing what it means. */
  description: string;
}

function hazardLabelOrNull(hazard: HazardKey | null): string | null {
  return hazard ? hazardKeyLabel(hazard) : null;
}

/** Shared shell: hairline accent along the top, eyebrow, value, description. */
function CardShell({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex min-w-[220px] flex-1 flex-col justify-between overflow-hidden rounded-xl border border-border bg-card p-5">
      <span className="absolute inset-x-0 top-0 h-1 bg-primary" />
      <div className="flex flex-col gap-3">
        <p className="text-xs font-bold uppercase leading-4 tracking-[0.08em] text-muted-foreground">{label}</p>
        {children}
      </div>
      <p className="pt-4 text-xs leading-5 text-muted-foreground">{description}</p>
    </div>
  );
}

function TakeawayCard({ label, value, description }: Takeaway) {
  return (
    <CardShell label={label} description={description}>
      {value !== null ? <p className="text-2xl font-bold leading-8">{value}</p> : <PendingScore />}
    </CardShell>
  );
}

function PercentAtHighRiskCard({ perHazardPct }: { perHazardPct: PerHazardHighRisk[] }) {
  return (
    <CardShell label="% at high risk, per hazard" description="Share of assets at risk 3 or 4, for each hazard">
      <HazardPercentList perHazardPct={perHazardPct} />
    </CardShell>
  );
}

/**
 * Flex-wrap, not a fixed-column grid — `lg:grid-cols-4` responds to the
 * *viewport* width, not this container's actual width. When this renders
 * inside a HorizonComparisonGrid column (which can be a third of the
 * viewport with 3 horizons active), `lg:` still fires because the browser
 * window is wide, cramming all 4 cards into a column far narrower than
 * they need — the same class of bug HorizonComparisonGrid itself was just
 * fixed for. `min-w` per card lets them wrap onto a new line once the
 * actual available width runs out, regardless of viewport size.
 */
export function TakeawayCards({ results, assets, stageImportance }: TakeawayCardsProps) {
  const takeaways = computeTakeaways(results, assets, stageImportance);

  return (
    <div className="flex flex-wrap gap-4">
      <TakeawayCard
        label="Most widespread hazard"
        value={hazardLabelOrNull(takeaways.mostAssetsAtRisk)}
        description="Hazard with most assets at high risk"
      />
      <TakeawayCard
        label="Worst for key assets"
        value={hazardLabelOrNull(takeaways.riskiestForTopAssets)}
        description="Hazard riskiest for top-importance assets"
      />
      <TakeawayCard
        label="Worst for top value chain stage"
        value={hazardLabelOrNull(takeaways.riskiestForTopValueChainStage)}
        description="Hazard riskiest for the most important value chain stage"
      />
      <PercentAtHighRiskCard perHazardPct={takeaways.percentAtHighRisk} />
    </div>
  );
}
